import { useRef } from "react";
import { ImagePlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const MAX_BYTES = 2 * 1024 * 1024;

function readAsBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export default function ImageUpload({ value, onChange, label = "Image", testid = "image-upload" }) {
    const inputRef = useRef(null);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_BYTES) {
            alert(`Image too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Max 2MB.`);
            return;
        }
        const dataUrl = await readAsBase64(file);
        onChange(dataUrl);
        if (inputRef.current) inputRef.current.value = "";
    };

    return (
        <div className="space-y-2" data-testid={testid}>
            <div className="text-sm font-semibold text-slate-700">{label}</div>
            {value ? (
                <div className="relative inline-block">
                    <img
                        src={value}
                        alt="preview"
                        className="w-40 h-40 object-cover rounded-md border border-slate-200"
                    />
                    <button
                        type="button"
                        onClick={() => onChange("")}
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-slate-950 text-white grid place-items-center shadow-md hover:bg-slate-800"
                        data-testid={`${testid}-remove`}
                    >
                        <X className="w-3 h-3" />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="w-40 h-40 rounded-md border-2 border-dashed border-slate-300 hover:border-slate-950 hover:bg-slate-50 grid place-items-center text-slate-500 transition-colors"
                    data-testid={`${testid}-trigger`}
                >
                    <div className="text-center">
                        <ImagePlus className="w-6 h-6 mx-auto" />
                        <div className="text-xs mt-2">Upload (max 2MB)</div>
                    </div>
                </button>
            )}
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={handleFile}
                className="hidden"
                data-testid={`${testid}-input`}
            />
            {value && (
                <div className="flex gap-2">
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => inputRef.current?.click()}
                    >
                        Replace
                    </Button>
                </div>
            )}
        </div>
    );
}
